import './overlay_back_to_og_v2.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchOverlayRows } from '../../shared/leaderboardShared';
import mascot from './mascot.webp';

const CANVAS_WIDTH = 1920;
const CANVAS_HEIGHT = 1080;
const PANEL_WIDTH = 619;
const DEFAULT_PAGE_SIZE = 10;
const MAX_PAGE_SIZE = 20;
const MAX_PANEL_HEIGHT = 1032;
const MAX_ENTRIES = 50;
const PAGE_INTERVAL_MS = 15000;
const REFRESH_ALIVE_MS = 2000;
const REFRESH_ALL_MS = 5000;
const HIT_MS = 800;
const LEAVE_MS = 560;
const ENTER_STAGGER_MS = 60;
const ROWS_TOP = 246;
const SLOT_HEIGHT = 58;
const PANEL_BOTTOM = 22;
const NAME_FONT_MAX = 26;
const NAME_FONT_MIN = 9;
const NAME_LETTER_SPACING_EM = 0;
const NAME_SAFETY_PX = 6;
const FLAG_WIDTH_EM = 1.15;
const FLAG_MARGIN_EM = 0.3;
const FONT_STACK = "'Obviously Black Italic', 'OmnesBlack', sans-serif";
const PAGE_TITLE = 'Overlay Back To OG';
const BLANK_ICON = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const DEMO_TICK_MS = 3200;
const DEMO_MIN_ALIVE = 3;
const DEMO_RESET_TICKS = 3;

const DEMO_TEAMS = [
  ['Zmatix', 'Kaoz'],
  ['Vortex', 'Nyxie'],
  ['Aqualix_FN', 'Sh4dow'],
  ['Tryhard Tom', 'Bugha Jr'],
  ['Elyon', 'Rekkz'],
  ['Pandaa', 'Mistik'],
  ['Skyz', 'Wexo'],
  ['xXDarkSasukeXx', 'LeMeilleurDuMonde'],
  ['Kenzo', 'Lyra'],
  ['Nox', 'Fury'],
  ['Oxy', 'Pulsar'],
  ['Hydra', 'Kami'],
  ['Saiko', 'Vela'],
  ['Blitz', 'Orion'],
  ['Neko', 'Raze'],
  ['Mako', 'Tiz'],
  ['Jinx', 'Ember'],
  ['Sora', 'Kyro'],
].map((names, index) => {
  const teamName = [...names].sort((a, b) => a.localeCompare(b)).join(' + ');
  return {
    key: teamName,
    place: index + 1,
    points: Math.round(428 - index * 16.4 - (index % 3) * 3),
    teamName,
    members: null,
  };
});

function isTrue(value) {
  return value === 'true' || value === '1';
}

function parsePageSize(value) {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) {
    return DEFAULT_PAGE_SIZE;
  }
  return Math.min(MAX_PAGE_SIZE, Math.max(1, parsed));
}

function omitKey(source, key) {
  if (!(key in source)) {
    return source;
  }
  const next = { ...source };
  delete next[key];
  return next;
}

function buildDemoRows(aliveKeys) {
  return DEMO_TEAMS.map((team) => ({ ...team, alive: aliveKeys.has(team.key) }));
}

function sameFits(previous, next) {
  const previousKeys = Object.keys(previous);
  const nextKeys = Object.keys(next);
  return previousKeys.length === nextKeys.length
    && nextKeys.every((key) => previous[key]
      && previous[key].size === next[key].size
      && previous[key].scaleX === next[key].scaleX);
}

function rankTone(place) {
  if (place === 1) return 'tone-pink';
  if (place === 2) return 'tone-mint';
  if (place === 3) return 'tone-cyan';
  return 'tone-base';
}

function TeamName({ row }) {
  if (!row.members || !row.members.length) {
    return row.teamName;
  }

  return row.members.map((member, index) => (
    <React.Fragment key={`${member.name}-${index}`}>
      <img
        className='bto2-flag'
        src={member.flagUrl}
        alt=''
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = member.fallbackFlagUrl;
        }}
      />
      {member.name}
      {index < row.members.length - 1 ? ' + ' : ''}
    </React.Fragment>
  ));
}

function StatusLed() {
  return (
    <span className='bto2-dot' aria-hidden='true' />
  );
}

function OverlayRow({ row, phase, enterDelay, fit }) {
  const eliminationPhase = !row.alive && phase ? phase : '';
  const rowClassName = [
    'bto2-row',
    rankTone(row.place),
    row.alive ? 'is-alive' : 'is-dead',
    eliminationPhase ? `is-${eliminationPhase}` : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={`bto2-slot${eliminationPhase === 'leaving' ? ' is-leaving' : ''}`}
      data-row-key={row.key}
      style={{ '--enter-delay': `${enterDelay}ms` }}
    >
      <div className={rowClassName}>
        <div className='bto2-box bto2-box-rank'>
          <span>Top {row.place}</span>
        </div>
        <div className='bto2-status'>
          {(row.alive || eliminationPhase === 'hit') && <StatusLed />}
        </div>
        <div className='bto2-name'>
          <span
            className='bto2-name-inner'
            style={fit.scaleX < 1 ? { transform: `scaleX(${fit.scaleX})` } : undefined}
          >
            <span className='bto2-name-text' style={{ fontSize: `${fit.size}px` }}>
              <TeamName row={row} />
            </span>
          </span>
        </div>
        <div className='bto2-box bto2-box-points'>
          <span>{Math.round(Number(row.points) || 0)}<small>pts</small></span>
        </div>
      </div>
    </div>
  );
}

function OverlayBackToOGV2() {
  const search = useLocation().search;
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const leaderboardId = params.get('id') || '';
  const onlyAlive = isTrue(params.get('alive'));
  const showFlags = isTrue(params.get('flags'));
  const demo = isTrue(params.get('demo'));
  const pageSize = parsePageSize(params.get('per_page'));
  const soloParam = params.get('solo');
  const solo = soloParam === null ? typeof window.obsstudio === 'undefined' : isTrue(soloParam);
  const fullPanelHeight = ROWS_TOP + pageSize * SLOT_HEIGHT + PANEL_BOTTOM;

  const [rows, setRows] = useState([]);
  const [phases, setPhases] = useState({});
  const [page, setPage] = useState(0);
  const [scale, setScale] = useState(1);
  const [nameFits, setNameFits] = useState({});
  const previousAliveRef = useRef(null);
  const timersRef = useRef(new Set());
  const tokensRef = useRef(new Map());
  const onlyAliveRef = useRef(onlyAlive);
  const enterDelaysRef = useRef(new Map());
  const rowsLayerRef = useRef(null);

  useEffect(() => {
    onlyAliveRef.current = onlyAlive;
  }, [onlyAlive]);

  useEffect(() => {
    const previousTitle = document.title;
    const icons = Array.from(document.querySelectorAll("link[rel='icon'], link[rel='apple-touch-icon']"));
    const previousHrefs = icons.map((icon) => icon.getAttribute('href'));
    document.title = PAGE_TITLE;
    icons.forEach((icon) => icon.setAttribute('href', BLANK_ICON));
    return () => {
      document.title = previousTitle;
      icons.forEach((icon, index) => icon.setAttribute('href', previousHrefs[index]));
    };
  }, []);

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timerId) => window.clearTimeout(timerId));
      timers.clear();
    };
  }, []);

  const schedule = useCallback((callback, delay) => {
    const timerId = window.setTimeout(() => {
      timersRef.current.delete(timerId);
      callback();
    }, delay);
    timersRef.current.add(timerId);
  }, []);

  const startElimination = useCallback((key) => {
    const token = (tokensRef.current.get(key) || 0) + 1;
    tokensRef.current.set(key, token);
    const isCurrent = () => tokensRef.current.get(key) === token;

    setPhases((current) => ({ ...current, [key]: 'hit' }));
    schedule(() => {
      if (!isCurrent()) {
        return;
      }
      if (!onlyAliveRef.current) {
        setPhases((current) => omitKey(current, key));
        return;
      }
      setPhases((current) => (current[key] ? { ...current, [key]: 'leaving' } : current));
      schedule(() => {
        if (isCurrent()) {
          setPhases((current) => omitKey(current, key));
        }
      }, LEAVE_MS);
    }, HIT_MS);
  }, [schedule]);

  const applyRows = useCallback((nextRows) => {
    const previous = previousAliveRef.current;
    previousAliveRef.current = new Map(nextRows.map((row) => [row.key, row.alive]));
    setRows(nextRows);
    if (!previous) {
      return;
    }
    nextRows.forEach((row) => {
      if (!row.alive && previous.get(row.key) === true) {
        startElimination(row.key);
      }
    });
  }, [startElimination]);

  useEffect(() => {
    if (demo || !leaderboardId) {
      return undefined;
    }

    let cancelled = false;
    let timeoutId;
    previousAliveRef.current = null;

    const refresh = async () => {
      try {
        const data = await fetchOverlayRows({
          leaderboardId,
          showFlags,
          maxEntries: onlyAlive ? Number.POSITIVE_INFINITY : MAX_ENTRIES,
        });
        if (!cancelled) {
          applyRows(data.map((row) => ({ ...row, key: row.teamName, teamName: row.teamName.split(' - ').join(' + ') })));
        }
      } catch (error) {
      }
      if (!cancelled) {
        timeoutId = window.setTimeout(refresh, onlyAlive ? REFRESH_ALIVE_MS : REFRESH_ALL_MS);
      }
    };
    refresh();

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [applyRows, demo, leaderboardId, onlyAlive, showFlags]);

  useEffect(() => {
    if (!demo) {
      return undefined;
    }

    let aliveKeys = new Set(DEMO_TEAMS.map((team) => team.key));
    let waitedTicks = 0;
    previousAliveRef.current = null;
    applyRows(buildDemoRows(aliveKeys));

    const interval = window.setInterval(() => {
      if (aliveKeys.size <= DEMO_MIN_ALIVE) {
        waitedTicks += 1;
        if (waitedTicks >= DEMO_RESET_TICKS) {
          waitedTicks = 0;
          aliveKeys = new Set(DEMO_TEAMS.map((team) => team.key));
        }
      } else {
        const candidates = DEMO_TEAMS.filter((team) => aliveKeys.has(team.key)).slice(0, pageSize);
        const kills = Math.random() < 0.3 ? 2 : 1;
        aliveKeys = new Set(aliveKeys);
        for (let count = 0; count < kills && candidates.length; count += 1) {
          const [victim] = candidates.splice(Math.floor(Math.random() * candidates.length), 1);
          aliveKeys.delete(victim.key);
        }
      }
      applyRows(buildDemoRows(aliveKeys));
    }, DEMO_TICK_MS);

    return () => window.clearInterval(interval);
  }, [applyRows, demo, pageSize]);

  useEffect(() => {
    const targets = [document.documentElement, document.body];
    const previousStyles = targets.map((element) => element.getAttribute('style'));
    targets.forEach((element) => {
      element.style.background = 'transparent';
      element.style.overflow = 'hidden';
      if (solo) {
        element.style.width = '100%';
        element.style.height = '100%';
      }
    });
    return () => {
      targets.forEach((element, index) => {
        if (previousStyles[index] === null) {
          element.removeAttribute('style');
        } else {
          element.setAttribute('style', previousStyles[index]);
        }
      });
    };
  }, [solo]);

  useEffect(() => {
    const updateScale = () => {
      const next = solo
        ? Math.min(window.innerWidth / PANEL_WIDTH, window.innerHeight / fullPanelHeight)
        : Math.min(window.innerWidth / CANVAS_WIDTH, window.innerHeight / CANVAS_HEIGHT);
      setScale(Number.isFinite(next) && next > 0 ? next : 1);
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [fullPanelHeight, solo]);

  const rankedRows = useMemo(
    () => (onlyAlive ? rows : rows.slice(0, MAX_ENTRIES)),
    [rows, onlyAlive],
  );
  const totalPages = onlyAlive ? 1 : Math.max(1, Math.ceil(rankedRows.length / pageSize));

  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(0);
    }
  }, [page, totalPages]);

  useEffect(() => {
    if (totalPages <= 1) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      setPage((current) => (current + 1) % totalPages);
    }, PAGE_INTERVAL_MS);
    return () => window.clearTimeout(timeoutId);
  }, [page, totalPages]);

  const changePage = (step) => {
    if (totalPages <= 1) {
      return;
    }
    setPage((current) => (current + step + totalPages) % totalPages);
  };
  const canChangePage = totalPages > 1;

  const visibleRows = useMemo(() => {
    if (onlyAlive) {
      return rankedRows.filter((row) => row.alive || phases[row.key]).slice(0, pageSize);
    }
    return rankedRows.slice(page * pageSize, page * pageSize + pageSize);
  }, [onlyAlive, page, pageSize, phases, rankedRows]);

  const settledCount = visibleRows.filter((row) => row.alive || phases[row.key] !== 'leaving').length;
  const panelHeight = ROWS_TOP + settledCount * SLOT_HEIGHT + PANEL_BOTTOM;
  const panelScale = solo ? 1 : Math.min(1, MAX_PANEL_HEIGHT / fullPanelHeight);

  const enterDelays = enterDelaysRef.current;
  const visibleKeys = new Set(visibleRows.map((row) => row.key));
  Array.from(enterDelays.keys()).forEach((key) => {
    if (!visibleKeys.has(key)) {
      enterDelays.delete(key);
    }
  });
  let enteringCount = 0;
  visibleRows.forEach((row) => {
    if (!enterDelays.has(row.key)) {
      enterDelays.set(row.key, enteringCount * ENTER_STAGGER_MS);
      enteringCount += 1;
    }
  });

  useEffect(() => {
    let active = true;

    const recalculate = () => {
      const layer = rowsLayerRef.current;
      const context = document.createElement('canvas').getContext('2d');
      if (!active || !layer || !context) {
        return;
      }

      const measure = (text, size, flagCount) => {
        context.font = `${size}px ${FONT_STACK}`;
        const shown = String(text).toUpperCase();
        return context.measureText(shown).width
          + Math.max(0, shown.length - 1) * size * NAME_LETTER_SPACING_EM
          + flagCount * size * (FLAG_WIDTH_EM + FLAG_MARGIN_EM + NAME_LETTER_SPACING_EM)
          + 2;
      };

      const slots = new Map(
        Array.from(layer.querySelectorAll('[data-row-key]')).map((slot) => [slot.dataset.rowKey, slot]),
      );
      const fits = {};
      visibleRows.forEach((row) => {
        const nameElement = slots.get(row.key)?.querySelector('.bto2-name');
        if (!nameElement) {
          return;
        }
        const style = window.getComputedStyle(nameElement);
        const contentWidth = nameElement.clientWidth
          - (Number.parseFloat(style.paddingLeft) || 0)
          - (Number.parseFloat(style.paddingRight) || 0);
        const available = Math.max(12, contentWidth - NAME_SAFETY_PX);
        const flagCount = row.members ? row.members.length : 0;

        let size = NAME_FONT_MAX;
        const baseWidth = measure(row.teamName, size, flagCount);
        if (baseWidth > available) {
          size = Math.floor((available / baseWidth) * size * 0.97);
        }
        size = Math.max(NAME_FONT_MIN, Math.min(NAME_FONT_MAX, size));
        while (size > NAME_FONT_MIN && measure(row.teamName, size, flagCount) > available) {
          size -= 1;
        }
        const finalWidth = measure(row.teamName, size, flagCount);
        const scaleX = finalWidth > available ? Math.max(0.4, available / (finalWidth * 1.06)) : 1;
        fits[row.key] = { size, scaleX };
      });

      setNameFits((current) => (sameFits(current, fits) ? current : fits));
    };

    const frameId = window.requestAnimationFrame(recalculate);
    const fonts = document.fonts;
    if (fonts?.load) {
      fonts.load(`${NAME_FONT_MAX}px "Obviously Black Italic"`).then(recalculate).catch(() => {});
    }
    if (fonts?.ready) {
      fonts.ready.then(recalculate);
    }
    fonts?.addEventListener?.('loadingdone', recalculate);

    return () => {
      active = false;
      window.cancelAnimationFrame(frameId);
      fonts?.removeEventListener?.('loadingdone', recalculate);
    };
  }, [visibleRows]);

  return (
    <div
      className={`bto2-overlay${solo ? ' is-solo' : ''}`}
      style={{ transform: `scale(${scale})`, '--bto2-font': FONT_STACK }}
    >
      <div
        className='bto2-panel'
        style={{
          height: `${panelHeight}px`,
          transform: panelScale < 1 ? `scale(${panelScale})` : undefined,
        }}
      >
        <div className='bto2-backdrop' />
        <img className='bto2-mascot' src={mascot} alt='' />
        <div className='bto2-mini'>
          <span>Back to OG</span>
          <span>Inoxtag</span>
        </div>
        <div className='bto2-title'>Classement</div>

        <div className='bto2-head'>
          <div
            className={`bto2-head-label bto2-head-rank${canChangePage ? ' is-clickable' : ''}`}
            onClick={() => changePage(-1)}
          >
            #
          </div>
          <div className='bto2-head-label bto2-head-name'>Équipes</div>
          <div
            className={`bto2-head-label bto2-head-points${canChangePage ? ' is-clickable' : ''}`}
            onClick={() => changePage(1)}
          >
            Points
          </div>
        </div>

        <div className='bto2-rows' ref={rowsLayerRef}>
          {visibleRows.map((row) => (
            <OverlayRow
              key={row.key}
              row={row}
              phase={phases[row.key]}
              enterDelay={enterDelays.get(row.key) || 0}
              fit={nameFits[row.key] || { size: NAME_FONT_MAX, scaleX: 1 }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default OverlayBackToOGV2;
